/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */

import { TunePadCell } from '../cell';
import styles from './outline.module.css' with { type: 'css' };
import defs from '../../definitions.module.css' with { type: 'css' };
import html from './outline.module.html';
import { showAddCellDialog } from '../dialogs/add-cell-dialog';

/**
 * <tunepad-outline></tunepad-outline>
 */
export class ProjectOutline extends HTMLElement {

    static readonly ELEMENT = "tunepad-outline";

    static observedAttributes = [ ];

    private root : ShadowRoot;
    private menu : HTMLElement;
    private observer : MutationObserver;
    private ordering : string[] = [];

    private isReorderMode : boolean = false;
    private scrollContainer : HTMLElement | null = null;
    private reorderHoldTimeout : ReturnType<typeof setTimeout> | undefined;

    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles, defs);
        this.root.innerHTML = html;
        this.menu = this.root.querySelector('#cell-list') as HTMLElement;
        this.scrollContainer = this.root.querySelector('.outline-menu');
        this.observer = new MutationObserver((mutations, observer) => {
            let refresh = false;
            mutations.forEach(mutation => {
                if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
                    refresh = true;
                }
                else if (['tunepad-cell', 'tunepad-sample' ].includes(mutation.target.nodeName.toLowerCase())) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const cell = mutation.target as Element;

                        // highlight changed
                        if (cell.classList.contains('active')) {
                            this.root.querySelectorAll('li').forEach(c => c.classList.remove('active'));
                            this.root.querySelector(`.item[data-cell="${cell.getAttribute('uuid')}"]`)?.classList.add('active');
                        }
                    } else {
                        refresh = true;
                    }
                }
            });
            if (refresh) this.rebuildMenu();
        });
    }


    async connectedCallback() {
        const project = this.root.host.parentElement?.querySelector('tunepad-cell-list');
        if (project) {
            this.observer.observe(project, { childList: true, subtree: true, attributes: true });
            this.rebuildMenu();
        }
        this.root.querySelector('.add-button')?.addEventListener('click', e => {
            showAddCellDialog((cellType : string) => {
                this.dispatchEvent(new CustomEvent("add-cell", {
                    bubbles: false, composed: true, detail : { instrument : cellType }
                }));
            });
        });
        this.root.querySelector('.done-button')?.addEventListener('click', e => {
           this.exitReorderMode()
        });

        // reordering logic
        const REORDER_LONG_PRESS = 500; // ms
        const REORDER_SCROLL_THRESHOLD = 8; // px - cancel if user is scrolling

        let reorderStartY = 0;
        let reorderCancelled = false;

        this.scrollContainer?.addEventListener('pointerdown', (e) => {
           reorderStartY = e.clientY;
           reorderCancelled = false;

           this.reorderHoldTimeout = setTimeout(() => {
              if(!reorderCancelled && !this.isReorderMode) {
                  this.enterReorderMode();
              }
           }, REORDER_LONG_PRESS);
        });

        this.scrollContainer?.addEventListener('pointermove', (e)=>{
            if (this.isReorderMode) return;
            if(Math.abs(e.clientY - reorderStartY) > REORDER_SCROLL_THRESHOLD) {
               reorderCancelled = true;
               clearTimeout(this.reorderHoldTimeout);
           }
        });

        this.scrollContainer?.addEventListener('pointerup', (e) => {
           clearTimeout(this.reorderHoldTimeout);
        });

        this.scrollContainer?.addEventListener('pointercancel', (e) => {
            reorderCancelled = true;
            clearTimeout(this.reorderHoldTimeout);
        });
    }


    disconnectedCallback() {
        this.observer.disconnect();
    }

    attributeChangedCallback(name : string, oldValue : string, newValue : string) {  }


    public rebuildMenu() {
        //const project = this.root.host.parentElement?.querySelector('tunepad-cell-list');
        this.menu.innerHTML = '';
        this.ordering = [];

        // create ordered list of <tunepad-cell> elements
        const cells = this.root.host.parentElement!.querySelectorAll('tunepad-cell, tunepad-sample');
        cells.forEach(cell => this.addCell(cell));
    }


    private addCell(cell : Element) {
        function setName(name : string) {
            if (cell instanceof TunePadCell) {
                cell.setName(name);
                cell.emitUpdateEvent('name', name);
            }
        }

        // keep track of the current cell ordering
        this.ordering.push(cell.getAttribute('uuid')!);

        const instrument = (cell instanceof TunePadCell) ? cell.instrument : 'sound';

        const li = document.createElement('li');
        li.classList.add('item', instrument);
        li.setAttribute('data-cell', `${cell.getAttribute('uuid')}`);

        const icon = document.createElement('div');
        icon.classList.add('icon');
        icon.setAttribute('draggable', 'false'); //iOS specific, suppresses drag preview image

        const name = document.createElement('input');
        name.classList.add('name');
        name.setAttribute('spellcheck', 'false');
        name.value = cell.getAttribute('name') || '[untitled]';
        if (cell instanceof TunePadCell) {
            name.addEventListener('blur', e => setName(name.value));
            name.addEventListener('keypress', e => { if (e.key === "Enter") name.blur() });
            name.addEventListener('pointerdown', e => { e.stopImmediatePropagation(); });
        } else {
            name.readOnly = true;
        }

        // prevent iOS from showing a drag preview image when (accidentally) dragging the name
        name.addEventListener('touchstart', (e) => {
            if (this.isReorderMode) e.preventDefault();
        }, { passive: false });

        li.append(icon, name);
        this.menu.append(li);
        if (cell.classList.contains('active')) {
            li.classList.add('active');
        }

        //-------------------------------------------------------------------
        // on pointer down we select the given cell
        //-------------------------------------------------------------------
        const DRAG_THRESHOLD = 5; // px
        let isDragging = false;
        let startY = 0;
        let spacer : HTMLElement | undefined;

        li.addEventListener('pointerdown', (e) => {
            if(e.pointerType === 'mouse' && e.button !== 0) return;

            isDragging = true;
            startY = e.clientY;// - li.offsetTop;

            // only capture pointer when in reorder mode
            if(this.isReorderMode) {
                li.setPointerCapture(e.pointerId);
            }

            document.querySelectorAll('tunepad-cell, tunepad-sample').forEach(c => c.classList.remove('active'));
            this.root.querySelectorAll('li').forEach(c => c.classList.remove('active'));
            cell.classList.add('active');
            li.classList.add('active');
            //cell.recompile(true);
        });

        //-------------------------------------------------------------------
        // on pointer drag see if there has been enough of a vertical change to reorder
        //-------------------------------------------------------------------
        li.addEventListener('pointermove', (e) => {
            if (!isDragging || !this.isReorderMode) return;

            e.preventDefault();
            e.stopPropagation();

            const deltaY = e.clientY - startY;
            const torn = li.classList.contains('dragging');

            if (Math.abs(deltaY) > DRAG_THRESHOLD && !torn) {
                li.classList.add('dragging');
                spacer = document.createElement('li');
                spacer.classList.add('spacer');
                li.after(spacer);
            }

            if (torn && spacer) {
                const y = e.clientY;
                li.style.top = `${y - 100}px`;
                this.menu.childNodes.forEach(node => {
                    const child = node as HTMLElement;
                    if (child.classList.contains('item')) {
                        const rect = child.getBoundingClientRect();
                        if (child === this.menu.childNodes[0] && y < rect.top + 20) {
                            spacer?.remove();
                            child.before(spacer!);
                        }
                        else if (y > rect.top + rect.height / 2 && y < rect.bottom) {
                            spacer?.remove();
                            child.after(spacer!);
                        }
                    }
                })
            }
        }, { passive: false }); // passive = false for iOS


        li.addEventListener('pointerup', (e) => {
            const wasReOrdering = li.classList.contains('dragging');
            const wasTap = isDragging && !wasReOrdering && !this.isReorderMode;

            if(li.hasPointerCapture(e.pointerId)) li.releasePointerCapture(e.pointerId);

            isDragging = false;
            li.classList.remove('dragging');
            spacer?.replaceWith(li);
            spacer?.remove();
            spacer = undefined;

            if(wasReOrdering){
                this.emitReorderEvent(`${li.getAttribute('data-cell')}`);
            } else if(wasTap) {
                cell.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });

        li.addEventListener('pointercancel', (e) => {
            isDragging = false;
            li.classList.remove('dragging');
            spacer?.replaceWith(li);
            spacer?.remove();
            spacer = undefined;
        })

        li.addEventListener('contextmenu', (e) => {
           e.preventDefault();
        });

        // touch start and touch move prevent iOS/iPadOS specific UI elements from showing up during dragging-to-reorder
        li.addEventListener('touchstart', (e) => {
            if (this.isReorderMode) e.preventDefault();
        }, { passive: false });

        li.addEventListener('touchmove', (e) => {
            if (this.isReorderMode) e.preventDefault();
        }, { passive: false });
    }

    private enterReorderMode() {
        this.isReorderMode = true;
        this.scrollContainer?.classList.add('reorder-mode');

        // exit reorder mode if user taps outside the panel
        const onOutsideTap = (e : PointerEvent) => {
            // checking against the host element and not the shadow container
            // because the shadow DOM retargets events at document level
            if(!this.contains(e.target as Node)) {
                this.exitReorderMode();
                document.removeEventListener('pointerdown', onOutsideTap);
            }
        };
        document.addEventListener('pointerdown', onOutsideTap);
    }

    private exitReorderMode() {
        this.isReorderMode = false;
        this.scrollContainer?.classList.remove('reorder-mode');

        // clean any in-progress reorder/drag
        this.menu.querySelectorAll('.item').forEach(item => {
            (item as HTMLElement).style.touchAction = '';
            item.classList.remove('dragging');
        });
        this.menu.querySelectorAll('.spacer').forEach(spacer => spacer.remove());
    }

    private emitReorderEvent(moved : string) {
        function changed(a : string[], b : string[]) : boolean {
            if (a.length !== b.length) return true;
            return !(a.every((value, i) => value === b[i]));
        }

        const reordering : string[] = [];
        this.menu.querySelectorAll('.item').forEach(el => {
            reordering.push(el.getAttribute('data-cell') ?? '');
        });

        if (changed(this.ordering, reordering)) {
            this.ordering = reordering;
            this.dispatchEvent(new CustomEvent("reorder-cells", {
                bubbles: false,
                composed: true,
                detail: { 
                    ordering : reordering,
                    moved : moved,
                    newPosition : reordering.indexOf(moved)
                }
            }));
            setTimeout(() => {
                document.querySelector(`[uuid="${moved}"]`)?.
                    scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);
        }
    }

}

